import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Download, Filter, RotateCcw, ShieldAlert } from "lucide-react";
import PageHeader from "@/components/PageHeader";

const PAGE_SIZE = 50;

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

export default function Logs() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ user_email: "", entity_type: "", action: "", date_from: "", date_to: "" });
  const [opts, setOpts] = useState({ actors: [], entity_types: [], actions: [] });
  const [suspicious, setSuspicious] = useState({ items: [], threshold: 5, window_minutes: 60 });

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters, page]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/logs", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Loglar yüklenemedi"); }
    finally { setLoading(false); }
  };
  const loadOptions = async () => {
    try {
      const { data } = await api.get("/logs/filters");
      setOpts({
        actors: data.actors || [],
        entity_types: data.entity_types || [],
        actions: data.actions || [],
      });
    } catch { /* sessizce yut */ }
  };
  const loadSuspicious = async () => {
    try {
      const { data } = await api.get("/logs/suspicious", { params: { window_minutes: 60, threshold: 5 } });
      setSuspicious(data || { items: [], threshold: 5, window_minutes: 60 });
    } catch { /* sessizce yut */ }
  };

  useEffect(() => { loadOptions(); loadSuspicious(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  const reset = () => {
    setFilters({ user_email: "", entity_type: "", action: "", date_from: "", date_to: "" });
    setPage(1);
  };

  const download = async () => {
    try {
      const search = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) search.append(k, v); });
      const res = await api.get(`/logs/export-xlsx?${search.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `loglar_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Excel indirilemedi"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="logs-page">
      <PageHeader kicker="Sistem" title="Aktivite Logları" />

      {/* Şüpheli Aktivite Uyarı Bandı */}
      {suspicious.items.length > 0 && (
        <div className="border-2 border-red-600 bg-red-50 p-4" data-testid="logs-suspicious-banner">
          <div className="flex items-start gap-3">
            <ShieldAlert size={20} className="text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-red-900">
                Şüpheli Aktivite Tespit Edildi
                <span className="ml-2 inline-block px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-mono rounded" data-testid="logs-suspicious-count">
                  {suspicious.items.length} IP
                </span>
              </div>
              <div className="text-xs text-red-800 mt-0.5">
                Son <span className="font-mono">{suspicious.window_minutes}</span> dakika içinde <span className="font-mono">{suspicious.threshold}+</span> başarısız giriş denemesi.
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {suspicious.items.map((s) => (
                  <button
                    key={s.ip || "unknown"}
                    onClick={() => {
                      setPage(1);
                      setFilters({ ...filters, action: "login_failed", user_email: "" });
                    }}
                    title={s.emails.length ? `Denenen: ${s.emails.join(", ")}` : ""}
                    className="inline-flex items-center gap-2 border-2 border-red-600 bg-white hover:bg-red-600 hover:text-white px-2 py-1 text-xs font-mono transition-colors"
                    data-testid={`logs-suspicious-ip-${s.ip || "unknown"}`}
                  >
                    <span>{s.ip || "IP yok"}</span>
                    <span className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] rounded">{s.count}×</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtreler */}
      <div className="bg-white border-2 border-[#09090B] p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs font-mono uppercase text-neutral-500">
          <Filter size={14} /> Filtreler
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] font-mono uppercase">Kullanıcı</Label>
            <select
              className="w-full h-10 rounded-none border-2 border-[#09090B] px-2 text-sm bg-white"
              value={filters.user_email}
              onChange={(e) => { setPage(1); setFilters({ ...filters, user_email: e.target.value }); }}
              data-testid="logs-filter-user"
            >
              <option value="">Tümü</option>
              {opts.actors.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase">Modül</Label>
            <select
              className="w-full h-10 rounded-none border-2 border-[#09090B] px-2 text-sm bg-white"
              value={filters.entity_type}
              onChange={(e) => { setPage(1); setFilters({ ...filters, entity_type: e.target.value }); }}
              data-testid="logs-filter-entity"
            >
              <option value="">Tümü</option>
              {opts.entity_types.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase">İşlem Türü</Label>
            <select
              className="w-full h-10 rounded-none border-2 border-[#09090B] px-2 text-sm bg-white"
              value={filters.action}
              onChange={(e) => { setPage(1); setFilters({ ...filters, action: e.target.value }); }}
              data-testid="logs-filter-action"
            >
              <option value="">Tümü</option>
              {opts.actions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase">Başlangıç</Label>
            <Input type="date" value={filters.date_from}
              onChange={(e) => { setPage(1); setFilters({ ...filters, date_from: e.target.value }); }}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
              data-testid="logs-filter-from" />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase">Bitiş</Label>
            <Input type="date" value={filters.date_to}
              onChange={(e) => { setPage(1); setFilters({ ...filters, date_to: e.target.value }); }}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
              data-testid="logs-filter-to" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-between pt-1">
          <div className="text-xs text-neutral-500">Toplam <span className="font-mono">{total}</span> kayıt · sayfa <span className="font-mono">{page}/{totalPages}</span></div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={reset} data-testid="logs-reset">
              <RotateCcw size={14} className="mr-2" /> Sıfırla
            </Button>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={download} data-testid="logs-export">
              <Download size={14} className="mr-2" /> Excel İndir
            </Button>
          </div>
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Tarih / Saat</TableHead>
              <TableHead className="font-mono uppercase text-xs">Kullanıcı</TableHead>
              <TableHead className="font-mono uppercase text-xs">Modül</TableHead>
              <TableHead className="font-mono uppercase text-xs">İşlem</TableHead>
              <TableHead className="font-mono uppercase text-xs">Detay</TableHead>
              <TableHead className="font-mono uppercase text-xs">Kayıt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-neutral-500 italic">
                  {loading ? "Yükleniyor…" : "Kayıt bulunamadı"}
                </TableCell>
              </TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`logs-row-${r.id}`}>
                <TableCell className="font-mono text-xs whitespace-nowrap">{fmtDT(r.at)}</TableCell>
                <TableCell className="text-sm">
                  <div className="font-medium truncate max-w-[200px]" title={r.actor_name}>{r.actor_name || "-"}</div>
                  <div className="text-[10px] text-neutral-500 truncate max-w-[200px]" title={r.actor_email}>{r.actor_email}</div>
                </TableCell>
                <TableCell>
                  <span className="inline-block px-2 py-0.5 border border-[#09090B] text-[10px] font-mono uppercase">
                    {r.entity_type_label}
                  </span>
                </TableCell>
                <TableCell className="text-sm">
                  <div>{r.label || r.action_label}</div>
                  {(r.ip || r.user_agent) && (
                    <div className="text-[10px] text-neutral-500 mt-0.5 font-mono truncate max-w-[280px]" title={`${r.ip} · ${r.user_agent}`}>
                      {r.ip && <span className="mr-1">IP: {r.ip}</span>}
                      {r.user_agent && <span className="italic">{r.user_agent.slice(0, 60)}{r.user_agent.length > 60 ? "…" : ""}</span>}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs text-neutral-600">
                  {(r.old || r.new) ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      {r.field && <span className="text-[10px] font-mono uppercase text-neutral-500">{r.field}</span>}
                      <span className="font-mono text-neutral-500">{r.old || "—"}</span>
                      <span className="text-neutral-400">→</span>
                      <span className="font-mono font-semibold text-neutral-900">{r.new || "—"}</span>
                    </div>
                  ) : (
                    <span className="text-neutral-400">—</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-[10px] text-neutral-500 truncate max-w-[160px]" title={r.entity_id}>{r.entity_id || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="logs-prev">Önceki</Button>
          <div className="text-xs font-mono">{page} / {totalPages}</div>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="logs-next">Sonraki</Button>
        </div>
      )}
    </div>
  );
}
